import { ColumnMetricDto, FilterMetricDto, FilterOperatorsDto, LogicalOperatorDto, ReportFilterGroupLinkDto, ReportColumnDto, ReportFilterGroupDto, ReportDto, Store, AggregationsDto, PrimaryCategory2, Reason, OrderTypesResponse, DataGroupDto } from '../../../app/services/api/generated';
import { TFunction } from 'i18next';
import { ChangeEvent, Dispatch, SetStateAction, createContext, useMemo, useState, SyntheticEvent } from 'react';
import { useReportsBuilder } from '../hooks/useReportsBuilder';
import { UserType } from '../../../components/shared/useUser'; 
import { Blocker } from 'react-router-dom';
import { TypesRecord } from '../../../pages/Transactions/types/TypesRecord';

  type GroupReasons = {
  [groupId: number]: Reason[] | undefined;  
};
export interface ReportBuilderValues {
    t: TFunction<'reportsBuilder', undefined>,
    dataGroups: DataGroupDto[] | undefined;
    selectedDataGroup: number | undefined;
    handleDataGroupChange: (dataGroupId: number) => void,
    columnMetrics: ColumnMetricDto[] | undefined;
    reportName: string,
    report: ReportDto  | undefined,
    setReport: Dispatch<SetStateAction<ReportDto  | undefined>>,
    setColumnMetrics: Dispatch<SetStateAction<ColumnMetricDto[] | undefined>>,
    setColumns: Dispatch<SetStateAction<ReportColumnDto[]>>,
    handleColumnChange: (index: number, value: string) => void,
    removeColumn: (index: number) => void,
    columns: ReportColumnDto[],
    getColumnMetricsList: (shouldShowMetrics: number) => void
    addFilterGroup: () => void,
    addFilter: (groupId: number | undefined) => void,
    removeFilter: (groupId: number | undefined, filterId: number) => void,
    handleFilterChange: (groupId: number | undefined, filterUiId: number | undefined, field: string, value: string) => void,
    removeFilterGroup: (groupId: number | undefined) => void,
    handleReportChange: (event: ChangeEvent<HTMLInputElement>) => void,
    getLogicalOperatorsList: () => void,
    getFilterMetricsList: () => void,
    getFilterOperatorList: () => void,
   handleSaveReport: (forceSave?: boolean) => void
    handleFilterLogicalOperatorChange: (groupId: number | undefined, filterUiId: number | undefined, value: string) => void,
    handleGroupLogicalOperatorChange: (groupId: number | undefined, value: string) => void,
    filterGroups: ReportFilterGroupDto[] | undefined,
    filterMetrics: FilterMetricDto[] | undefined,
    logicalOperator: LogicalOperatorDto[] | undefined,
    filterOperator: FilterOperatorsDto[] | undefined,
    fetchReportData: () => void,
    groupLogicalOperatorIds: Record<number, number>,
    setGroupLogicalOperatorIds: Dispatch<SetStateAction<Record<number, number>>>,
    getMetricNameById: (metricId: number) => void,
    handleAddFilterGroup: () => void,
    filterGroupLinks: ReportFilterGroupLinkDto[]
    disableCreateReport: boolean,
    openInformationDialog: boolean,
    handleClose: () => void,
    informationMessage: string,
    isEdit: boolean,
    handleUpdateReport: () => void,
    handleLinkClick: () => void,
    openGoBackDialog: boolean,
    handleGoBackConfirm: () => void 
    fromDate: string,
    toDate: string,
    getMaxDate: () => string,
    getMinDate: () => string,
    openGenerate: () => void,
    openGenerateDialog: boolean,
    user: UserType | null,
    handleSelectChange: (event: SyntheticEvent, value: Store[]) => void,
    handleSelectAllChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
    selectedStores: Store[],
    checkedAllStores: boolean,
    reportAggregations: AggregationsDto[],
    handleAggregateChange: (value: number) => void,
    reportAggregationId: number | undefined,
    loading: boolean,
    handleGenerateReport: () => void,
    allCategories: PrimaryCategory2[], 
    transactionTypes: TypesRecord[],
    reasons: Reason[],
    filterOperatorsMap: Map<number, FilterOperatorsDto[]>,
    isMobile: boolean,
    isError: boolean,
    informationModalTitle: string,
    handleUpdateReportConfirmed: () => void,
    groupReasons: GroupReasons,
    isGenerateDisabled: boolean,
    ordersType: OrderTypesResponse, 
    invalidFilters: {
    groupId: number;
    filterId?: number | undefined;
    field: 'Metric' | 'Operator' | 'Value' | 'LogicalOperator' | 'GroupLogicalOperator' | 'ConflictingLogic';
    message?: string;}[], 
    snackbarMessage: string,
    snackBarSeverity: 'error' | 'success' | 'warning',
    isSnackBarOpen: boolean,
    closeSnackBar: () => void,
    setFromDate: Dispatch<SetStateAction<string>>,
    setFromDateError: Dispatch<SetStateAction<boolean>>,
    fromDateError: boolean,
    setToDate: Dispatch<SetStateAction<string>>,
    toDateError: boolean,
    setToDateError: Dispatch<SetStateAction<boolean>>,
    hasChanges: boolean,
    selectedStore: Store | null | undefined,
    hasFilterConflict: boolean,
    markAsChanged: () => void, 
    handleConfirmSave: () => void,
    openSaveReportModal: boolean,
    canNotSaveMessage: string,
    isDuplicated: boolean,
    navigateToReports: () => void,
    blocker: Blocker,
}

export const ReportsBuilderContext = createContext<null | ReportBuilderValues>(null);

interface ProviderProps {
    children: React.ReactNode;
}

export const ReportsBuilderProvider = ({ children }: ProviderProps) => {
  const {
    t,
    dataGroups,
    selectedDataGroup,
    handleDataGroupChange,
    columnMetrics,
    columns,
    reportName,
    filterGroups,
    filterOperator,
    logicalOperator,
    filterMetrics,
    report,
    disableCreateReport,
    setReport,
    getLogicalOperatorsList,
    getFilterOperatorList,
    getFilterMetricsList,
    setColumnMetrics,
    setColumns,
    handleColumnChange,
    removeColumn,
    getColumnMetricsList,
    addFilterGroup,
    addFilter,
    removeFilter,
    handleFilterChange,
    handleFilterLogicalOperatorChange,
    removeFilterGroup,
    handleReportChange,
    handleSaveReport,
    handleGroupLogicalOperatorChange,
    fetchReportData,
    getMetricNameById,
    handleAddFilterGroup,
    handleClose,
    filterGroupLinks,
    openInformationDialog,
    informationMessage,
    isEdit,
    handleUpdateReport,
    handleLinkClick,
    openGoBackDialog,
    handleGoBackConfirm, 
    fromDate,
    toDate,
    getMaxDate,
    getMinDate,
    openGenerate,
    openGenerateDialog,
    user,
    handleSelectChange,
    handleSelectAllChange,
    checkedAllStores,
    selectedStores,
    reportAggregations,
    handleAggregateChange,
    reportAggregationId,
    loading,
    handleGenerateReport,
    allCategories, 
    transactionTypes,
    reasons,
    filterOperatorsMap,
    isMobile,
    isError,
    informationModalTitle,
    handleUpdateReportConfirmed,
    groupReasons,
    isGenerateDisabled,
    ordersType, 
    invalidFilters,
    closeSnackBar,
    isSnackBarOpen,
    snackBarSeverity,
    snackbarMessage, 
    setFromDate,
    setFromDateError,
    fromDateError,
    setToDate,
    toDateError,
    setToDateError, 
    hasChanges,
    selectedStore,
    hasFilterConflict,   
    handleConfirmSave,
    openSaveReportModal,
    canNotSaveMessage,
    isDuplicated,
    navigateToReports,
    markAsChanged,
    blocker,

  } = useReportsBuilder();

  const [groupLogicalOperatorIds, setGroupLogicalOperatorIds] = useState<Record<number, number>>({});

  const passedValue = {
    t,
    dataGroups,
    selectedDataGroup,
    handleDataGroupChange,
    columnMetrics,
    columns,
    reportName,
    filterGroups,
    filterOperator,
    logicalOperator,
    filterMetrics,
    report,
    disableCreateReport,
    setReport,
    getLogicalOperatorsList,
    getFilterOperatorList,
    getFilterMetricsList,
    setColumnMetrics,
    setColumns,
    handleColumnChange,
    removeColumn,
    getColumnMetricsList,
    addFilterGroup,
    addFilter,
    removeFilter,
    handleFilterChange,
    handleFilterLogicalOperatorChange,
    removeFilterGroup,
    handleReportChange,
    handleSaveReport,
    handleGroupLogicalOperatorChange,
    fetchReportData,
    groupLogicalOperatorIds,
    setGroupLogicalOperatorIds,
    getMetricNameById,
    handleAddFilterGroup, 
    handleClose,
    filterGroupLinks,
    openInformationDialog,
    informationMessage,
    isEdit,
    handleUpdateReport,
    handleLinkClick,
    openGoBackDialog,
    handleGoBackConfirm, 
    fromDate,
    toDate,
    getMaxDate,
    getMinDate,
    openGenerate,
    openGenerateDialog,
    user,
    handleSelectChange,
    handleSelectAllChange,
    selectedStores,
    checkedAllStores,
    reportAggregations,
    handleAggregateChange,
    reportAggregationId,
    loading,
    handleGenerateReport,
    allCategories, 
    transactionTypes,
    reasons,
    filterOperatorsMap,
    isMobile,
    isError,
    informationModalTitle,
    handleUpdateReportConfirmed,
    groupReasons,
    isGenerateDisabled,
    ordersType, 
    invalidFilters,
    closeSnackBar,
    isSnackBarOpen,
    snackBarSeverity,
    snackbarMessage, 
    setFromDate,
    setFromDateError,
    fromDateError,
    setToDate,
    toDateError,
    setToDateError, 
    hasChanges,
    selectedStore,
    hasFilterConflict,  
    handleConfirmSave,
    openSaveReportModal,
    canNotSaveMessage,
    isDuplicated,
    navigateToReports,
    markAsChanged,
    blocker,
  };

  const fireBaseProviderValue = useMemo(() => ({ passedValue }), [passedValue]);

  return (
    <ReportsBuilderContext.Provider value={fireBaseProviderValue.passedValue}>
      {children}
    </ReportsBuilderContext.Provider>
  );
};
